import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, AlertCircle, CheckCircle2, FileVideo2, FileText, GitBranch } from "lucide-react";
import { ShimmerButton } from "@/components/sim/shared";
import { apiCall, ApiError } from "@/lib/api";

export interface NodeDetail {
  id: string;
  sequenceNo: number;
  nodeCode: string;
  title: string | null;
  questionType: string;
  scenarioText: string;
  consequenceText: string | null;
  minOpenTextLength: number | null;
  evaluatorPrompt: string | null;
  videoUrl: string | null;
  videoTitle: string | null;
  videoInstruction: string | null;
  videoHasQuestion: boolean;
  documentUrl: string | null;
  documentTitle: string | null;
  nextNodeHighId: string | null;
  nextNodeMediumId: string | null;
  nextNodeLowId: string | null;
}

export interface ChoiceRow {
  id: string;
  nodeId: string;
  displayOrder: number;
  choiceText: string;
  score: number;
}

interface Props {
  simId: string;
  node: NodeDetail;
  /** All nodes in this sim — used to populate the branching target dropdowns. */
  allNodes: NodeDetail[];
  /** Choices belonging to THIS node (already filtered by the parent). */
  nodeChoices: ChoiceRow[];
  onClose: () => void;
  onSaved: () => void;
}

/**
 * If the author pastes a full <iframe ...> tag (common when copying from
 * SharePoint Stream / YouTube "Copy embed code"), pull the src attribute out
 * and store just that. Otherwise pass the value through unchanged.
 */
function extractVideoUrl(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed.toLowerCase().includes("<iframe")) return raw;
  const m = trimmed.match(/\bsrc\s*=\s*["']([^"']+)["']/i);
  return m !== null && typeof m[1] === "string" ? m[1] : raw;
}

/**
 * NodeEditModal — per-node edit. Fields:
 *   - title, scenario, consequence
 *   - evaluator_prompt + min_open_text_length (OpenText only)
 *   - video URL + title + instruction + has-question
 *   - document URL + title
 *   - branching: high/med/low next-node pickers (dropdowns of other nodes in
 *     the same sim, plus "(terminal — final decision)")
 *
 * Submits to POST /api/libraryNodeUpdate. On success, calls onSaved (which
 * re-pulls the sim detail in the parent) and closes.
 */
export function NodeEditModal({ simId, node, allNodes, nodeChoices, onClose, onSaved }: Props): JSX.Element {
  const isOpenText = node.questionType === "OpenText";

  const [choices, setChoices] = useState<ChoiceRow[]>(
    [...nodeChoices].sort((a, b) => a.displayOrder - b.displayOrder),
  );
  const [choiceBusy, setChoiceBusy] = useState(false);
  const [choiceError, setChoiceError] = useState<string | null>(null);
  const updateChoice = (id: string, patch: Partial<Pick<ChoiceRow, "choiceText" | "score">>): void => {
    setChoices((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  };

  const handleAddChoice = async (): Promise<void> => {
    if (isOpenText) return;
    setChoiceBusy(true);
    setChoiceError(null);
    try {
      const res = await apiCall<{ id: string; displayOrder: number }>("/libraryChoiceCreate", {
        method: "POST",
        body: { nodeId: node.id },
      });
      // Append to local state immediately — the backend seeds "Option {letter}"
      // / score 5, so the row shows up without a round trip through the parent.
      const nextLetter = String.fromCharCode("A".charCodeAt(0) + choices.length);
      setChoices((prev) => [
        ...prev,
        {
          id: res.id,
          nodeId: node.id,
          displayOrder: res.displayOrder,
          choiceText: `Option ${nextLetter}`,
          score: 5,
        },
      ]);
      onSaved(); // updates the parent's "MC · N choices" counter
    } catch (err) {
      setChoiceError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Couldn't add option");
    } finally {
      setChoiceBusy(false);
    }
  };

  const handleDeleteChoice = async (choiceId: string): Promise<void> => {
    setChoiceBusy(true);
    setChoiceError(null);
    try {
      await apiCall("/libraryChoiceDelete", { method: "POST", body: { nodeId: node.id, choiceId } });
      setChoices((prev) => prev.filter((c) => c.id !== choiceId));
      onSaved();
    } catch (err) {
      setChoiceError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Couldn't delete option");
    } finally {
      setChoiceBusy(false);
    }
  };

  const [title, setTitle] = useState(node.title ?? "");
  const [scenarioText, setScenarioText] = useState(node.scenarioText);
  const [consequenceText, setConsequenceText] = useState(node.consequenceText ?? "");
  const [evaluatorPrompt, setEvaluatorPrompt] = useState(node.evaluatorPrompt ?? "");
  const [minOpenTextLength, setMinOpenTextLength] = useState<number>(node.minOpenTextLength ?? 100);
  const [videoUrl, setVideoUrl] = useState(node.videoUrl ?? "");
  const [videoTitle, setVideoTitle] = useState(node.videoTitle ?? "");
  const [videoInstruction, setVideoInstruction] = useState(node.videoInstruction ?? "");
  const [videoHasQuestion, setVideoHasQuestion] = useState(node.videoHasQuestion);
  const [documentUrl, setDocumentUrl] = useState(node.documentUrl ?? "");
  const [documentTitle, setDocumentTitle] = useState(node.documentTitle ?? "");
  const [nextHigh, setNextHigh] = useState(node.nextNodeHighId ?? "");
  const [nextMed, setNextMed] = useState(node.nextNodeMediumId ?? "");
  const [nextLow, setNextLow] = useState(node.nextNodeLowId ?? "");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Esc closes
  useEffect(() => {
    const h = (e: KeyboardEvent): void => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const otherNodes = allNodes.filter((n) => n.id !== node.id);

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const dirtyChoices = isOpenText
        ? []
        : choices
            .filter((c) => {
              const orig = nodeChoices.find((o) => o.id === c.id);
              if (orig === undefined) return false;
              return orig.choiceText !== c.choiceText || orig.score !== c.score;
            })
            .map((c) => ({ id: c.id, choiceText: c.choiceText, score: c.score }));

      await apiCall("/libraryNodeUpdate", {
        method: "POST",
        body: {
          id: node.id,
          simId,
          fields: {
            title: title.trim() === "" ? null : title,
            scenarioText,
            consequenceText: consequenceText.trim() === "" ? null : consequenceText,
            evaluatorPrompt: isOpenText ? (evaluatorPrompt.trim() === "" ? null : evaluatorPrompt) : null,
            minOpenTextLength: isOpenText ? minOpenTextLength : null,
            videoUrl: videoUrl.trim() === "" ? null : videoUrl,
            videoTitle: videoTitle.trim() === "" ? null : videoTitle,
            videoInstruction: videoInstruction.trim() === "" ? null : videoInstruction,
            videoHasQuestion,
            documentUrl: documentUrl.trim() === "" ? null : documentUrl,
            documentTitle: documentTitle.trim() === "" ? null : documentTitle,
            nextNodeHighId: nextHigh === "" ? null : nextHigh,
            nextNodeMediumId: nextMed === "" ? null : nextMed,
            nextNodeLowId: nextLow === "" ? null : nextLow,
          },
          ...(dirtyChoices.length > 0 ? { choices: dirtyChoices } : {}),
        },
      });
      setSaved(true);
      onSaved();
      setTimeout(() => onClose(), 700);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Save failed";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        key="overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 backdrop-blur-sm px-4 py-10"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-3xl overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#0c0e22] to-[#070818] text-white shadow-2xl"
        >
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-fuchsia-500 via-violet-500 to-cyan-400" />

          <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-white/10 bg-[#0c0e22]/80 px-6 py-4 backdrop-blur">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-fuchsia-300 font-mono">
                {node.nodeCode} · {isOpenText ? "Open Text" : "Multiple Choice"} · Decision {node.sequenceNo}
              </div>
              <h2 className="mt-1 text-lg font-bold leading-tight">{node.title ?? `Decision ${node.sequenceNo}`}</h2>
            </div>
            <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-white/50 transition-colors hover:bg-white/5 hover:text-white" aria-label="Close">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-6 px-6 py-5">
            {/* Core fields */}
            <div className="space-y-4">
              <Field label="Title">
                <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} />
              </Field>
              <Field label="Scenario text *" hint="Second person, no obvious right answer">
                <textarea value={scenarioText} onChange={(e) => setScenarioText(e.target.value)} rows={6} className={inputClass} />
              </Field>
              <Field label="Consequence text" hint="1-2 reflective sentences shown after answering">
                <textarea value={consequenceText} onChange={(e) => setConsequenceText(e.target.value)} rows={2} className={inputClass} />
              </Field>
              {isOpenText && (
                <>
                  <Field label="Evaluator prompt" hint="Per-question AI scoring rubric (HIGH/MEDIUM/LOW)">
                    <textarea value={evaluatorPrompt} onChange={(e) => setEvaluatorPrompt(e.target.value)} rows={6} className={`${inputClass} font-mono text-[12px]`} />
                  </Field>
                  <Field label="Minimum answer length (chars)">
                    <input type="number" min={20} max={2000} value={minOpenTextLength} onChange={(e) => setMinOpenTextLength(Math.max(20, Math.min(2000, Number(e.target.value))))} className={inputClass} />
                  </Field>
                </>
              )}
            </div>

            {/* Choices (MC nodes only) */}
            {!isOpenText && (
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                <h3 className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-200">
                  Choices &amp; scores ({choices.length})
                </h3>
                <p className="mt-1 text-[11px] text-white/40">
                  Each option scores 0&ndash;10. Score drives the band (8+ = HIGH, 5&ndash;7 = MEDIUM, 0&ndash;4 = LOW) and which next-node the learner branches to.
                </p>
                <div className="mt-3 space-y-2.5">
                  {choices.map((c, idx) => {
                    const letter = String.fromCharCode("A".charCodeAt(0) + idx);
                    const band = c.score >= 8 ? "HIGH" : c.score >= 5 ? "MED" : "LOW";
                    const bandColor = c.score >= 8 ? "text-emerald-300" : c.score >= 5 ? "text-amber-300" : "text-rose-300";
                    const canDelete = choices.length > 2;
                    return (
                      <div key={c.id} className="grid grid-cols-[auto_1fr_auto_auto_auto] items-start gap-3 rounded-lg border border-white/8 bg-white/[0.02] p-2.5">
                        <div className="mt-1.5 font-mono text-[11px] font-bold text-white/55">{letter}</div>
                        <textarea
                          value={c.choiceText}
                          onChange={(e) => updateChoice(c.id, { choiceText: e.target.value })}
                          rows={2}
                          className={`${inputClass} text-[13px]`}
                        />
                        <div className="flex flex-col items-center">
                          <label className="text-[9px] font-bold uppercase tracking-[0.14em] text-white/45">Score</label>
                          <input
                            type="number"
                            min={0}
                            max={10}
                            value={c.score}
                            onChange={(e) => updateChoice(c.id, { score: Math.max(0, Math.min(10, Math.round(Number(e.target.value) || 0))) })}
                            className="mt-1 w-16 rounded-md border border-white/15 bg-white/5 px-2 py-1.5 text-center text-sm font-bold text-white outline-none focus:border-fuchsia-400/60"
                          />
                        </div>
                        <div className={`mt-5 font-mono text-[10px] font-bold tracking-[0.14em] ${bandColor}`}>
                          {band}
                        </div>
                        <button
                          type="button"
                          onClick={() => void handleDeleteChoice(c.id)}
                          disabled={!canDelete || choiceBusy}
                          title={canDelete ? `Delete option ${letter}` : "MC nodes need at least 2 options"}
                          className="mt-3.5 rounded p-1 text-white/30 transition-colors hover:bg-rose-500/15 hover:text-rose-300 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 flex items-center justify-between gap-3">
                  <div className="text-[10.5px] text-rose-300">
                    {choiceError !== null && choiceError}
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleAddChoice()}
                    disabled={choiceBusy || choices.length >= 6}
                    title={choices.length >= 6 ? "Max 6 options per question" : "Add another option"}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-400/40 bg-emerald-500/10 px-3 py-1 text-[11px] font-semibold text-emerald-100 hover:border-emerald-400/60 hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    + Add option ({choices.length}/6)
                  </button>
                </div>
              </div>
            )}

            {/* Video */}
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <h3 className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-fuchsia-200">
                <FileVideo2 className="h-3.5 w-3.5" /> Video (optional — shown before the decision)
              </h3>
              <p className="mt-1 text-[11px] text-white/40">YouTube share/watch links, SharePoint Stream embed URLs, and direct .mp4/.webm files all work. If you paste a full &lt;iframe&gt; embed tag, we&rsquo;ll auto-extract the src.</p>
              <div className="mt-3 space-y-3">
                <Field label="Video URL">
                  <input
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(extractVideoUrl(e.target.value))}
                    placeholder="Paste a URL — or the entire <iframe ...> tag — and we'll handle it"
                    className={inputClass}
                  />
                </Field>
                {videoUrl !== "" && videoUrl.trim().toLowerCase().startsWith("<iframe") && (
                  <p className="text-[10.5px] leading-snug text-amber-300/80">
                    ⚠ Looks like a full &lt;iframe&gt; tag — we couldn&rsquo;t find a src attribute to extract. Paste just the URL between the quotes, or re-copy the embed code.
                  </p>
                )}
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <Field label="Video title"><input value={videoTitle} onChange={(e) => setVideoTitle(e.target.value)} className={inputClass} /></Field>
                  <div className="flex flex-col justify-end gap-1">
                    <label className="inline-flex cursor-pointer items-center gap-2 text-[12px] text-white/65">
                      <input type="checkbox" checked={videoHasQuestion} onChange={(e) => setVideoHasQuestion(e.target.checked)} className="accent-fuchsia-400" />
                      Video has a question to answer afterwards
                    </label>
                    <p className="text-[10.5px] leading-snug text-white/40">
                      {videoHasQuestion
                        ? "Learner watches the video, then answers the scored decision below."
                        : "Informational video — learner watches and auto-advances to the next node (no score)."}
                    </p>
                  </div>
                </div>
                <Field label="Video instruction" hint="Optional context shown above the player">
                  <textarea value={videoInstruction} onChange={(e) => setVideoInstruction(e.target.value)} rows={2} className={inputClass} />
                </Field>
              </div>
            </div>

            {/* Document */}
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <h3 className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-200">
                <FileText className="h-3.5 w-3.5" /> Document (optional — shown before the decision)
              </h3>
              <p className="mt-1 text-[11px] text-white/40">PDF / DOCX / browser-renderable URL. Loaded in an iframe with a dark-framed branded viewer.</p>
              <div className="mt-3 space-y-3">
                <Field label="Document URL">
                  <input value={documentUrl} onChange={(e) => setDocumentUrl(e.target.value)} placeholder="https://…/briefing.pdf" className={inputClass} />
                </Field>
                <Field label="Document title">
                  <input value={documentTitle} onChange={(e) => setDocumentTitle(e.target.value)} className={inputClass} />
                </Field>
              </div>
            </div>

            {/* Branching */}
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <h3 className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-violet-200">
                <GitBranch className="h-3.5 w-3.5" /> Branching — pick the next node per score band
              </h3>
              <p className="mt-1 text-[11px] text-white/40">Leave all three blank if this is the final decision.</p>
              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                <BranchPicker label="HIGH (8-10)" tone="#10B981" value={nextHigh} onChange={setNextHigh} options={otherNodes} />
                <BranchPicker label="MED (5-7)" tone="#F59E0B" value={nextMed} onChange={setNextMed} options={otherNodes} />
                <BranchPicker label="LOW (0-4)" tone="#F87272" value={nextLow} onChange={setNextLow} options={otherNodes} />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 z-10 flex items-center justify-end gap-3 border-t border-white/10 bg-[#0c0e22]/85 px-6 py-4 backdrop-blur">
            {error !== null && (
              <span className="inline-flex items-center gap-1.5 text-xs text-rose-300">
                <AlertCircle className="h-3.5 w-3.5" /> {error}
              </span>
            )}
            {saved && (
              <span className="inline-flex items-center gap-1.5 text-xs text-emerald-300">
                <CheckCircle2 className="h-3.5 w-3.5" /> Saved
              </span>
            )}
            <button type="button" onClick={onClose} className="rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-xs font-medium text-white hover:border-white/30 hover:bg-white/10">
              Cancel
            </button>
            <ShimmerButton onClick={() => void handleSave()}>
              {saving ? "Saving…" : "Save changes"}
            </ShimmerButton>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

const inputClass = "w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-fuchsia-400/60 focus:outline-none";

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.12em] text-white/55">{label}</label>
      {hint !== undefined && <span className="ml-2 text-[10px] text-white/30">{hint}</span>}
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function BranchPicker({ label, tone, value, onChange, options }: { label: string; tone: string; value: string; onChange: (v: string) => void; options: NodeDetail[] }): JSX.Element {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: tone }}>{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-2 text-[12px] text-white focus:border-fuchsia-400/60 focus:outline-none">
        <option value="" className="bg-[#0c0e22]">— Terminal (final decision)</option>
        {options.map((n) => (
          <option key={n.id} value={n.id} className="bg-[#0c0e22]">
            {n.nodeCode} · {(n.title ?? `Decision ${n.sequenceNo}`).slice(0, 60)}
          </option>
        ))}
      </select>
    </div>
  );
}
